import { AggregationPeriod, Reports, TotalVolumeByPeriod } from "@app/shared";
import { DateOptions, IConfiguredTrafficReport, TrafficReportProps } from "./IConfiguredTrafficReport";
import { useFetch } from "../../hooks";
import { useContext, useEffect } from "react";
import { ReportContext } from "./reportHelpers";
import { format } from "date-fns-tz";
import { Bar, ChartProps, Line } from "react-chartjs-2";
import { ReportLoadingErrorWrapper } from "./ReportLoadingErrorWrapper";
import { extLightThemeColors } from "@app/shared";



const MonthlyVolumeComparison: React.FC<TrafficReportProps & { data: TotalVolumeByPeriod[] }> = props => {
    const { reportConfig } = useContext(ReportContext);
    const { chartType, chartTimezone } = reportConfig;
    const { title, subTitle, data } = props;
    interface ComparisonDataset {
        label: string;
        data: number[]; // 7 days * 24 hours
    }

    function groupHourlyDataByMonth(
        data: TotalVolumeByPeriod[],
        timezone: string
    ): ComparisonDataset[] {
        const grouped: Record<string, number[]> = {};

        data.forEach((entry) => {
            const yearKey = format(entry.periodStart!, "yyyy", { timeZone: chartTimezone });

            // Initialize array for the week
            if (!grouped[yearKey]) {
                grouped[yearKey] = Array(12).fill(0); // 7 days * 24 hours
            }
            var index = parseInt(format(entry.periodStart!, 'MM', { timeZone: chartTimezone })) - 1;
            grouped[yearKey][index] += entry.count; // Sum volumes if overlapping
        });

        // Convert grouped data into datasets
        return Object.entries(grouped).map(([label, data]) => ({
            label,
            data,
        }));
    }
    const datasets = groupHourlyDataByMonth(data, chartTimezone).map((wd, idx) => ({
        label: wd.label,
        data: wd.data,
        backgroundColor: extLightThemeColors[idx],
        borderColor: extLightThemeColors[idx]
    }));


    const chartjsProps = {
        data: {
            labels: Array.from({ length: 12 }, (_, i) => {
                return format(new Date(2020, i, 1), 'MMMM');
            }),
            datasets
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: !!title,
                    text: !!!title ? undefined : typeof title !== 'string' ? title(data) : title,
                    font: {
                        size: 24,
                        weight: 'bold'
                    },
                },
                subtitle: {
                    display: !!subTitle,
                    text: !!!subTitle ? undefined : typeof subTitle !== 'string' ? subTitle(data) : subTitle,
                    font: {
                        size: 16,
                        weight: 'bold'
                    },
                },
                legend: {
                    display: true,
                    position: 'bottom',
                }
            },
            interaction: {
                mode: 'index', // Ensures all datasets for the same label are shown
                intersect: false, // Ensures tooltips show even if cursor isn't directly over a bar
            },
            scales: {
                y: {
                    beginAtZero: true,
                    stacked: false
                },
                x: {
                    stacked: false
                }
            }
        }
    } as Omit<ChartProps<"line" | "bar", (number | [number, number] | null)[], unknown>, "type">;

    return (
        <div style={{ position: 'relative', width: props.size.width, height: props.size.height }}>
            {chartType === 'line'
                ? <Line  {...chartjsProps as ChartProps<'line'>} ></Line>
                : <Bar  {...chartjsProps as ChartProps<'bar'>} ></Bar>}
        </div>
    );
}

export const MonthlyVolumeComparisonDataLoader: React.FC<TrafficReportProps> = props => {
    const { reportConfig, searchParams, setIsLoading } = useContext(ReportContext);
    const [data, error, { isLoading }] = useFetch(() => Reports.getTotalVolume(reportConfig.grouping, AggregationPeriod.Month, searchParams), [searchParams]);
    useEffect(() => setIsLoading(isLoading), [isLoading, setIsLoading]);
    return (<ReportLoadingErrorWrapper error={error} hasNoData={!!!data?.length} isLoading={isLoading} size={props.size} >
        <MonthlyVolumeComparison {...props} data={data!} />
    </ReportLoadingErrorWrapper>);
}

export const MonthlyComparisonReport = {
    name: "Monthly Volume Comparison Report",
    description: "This report shows the average daily volume for every month grouped by year for the specified period",
    component: MonthlyVolumeComparisonDataLoader,
    key: 'mvc',
    defaultChartType: 'bar',
    defaultRange: DateOptions.AllData,
    config: {
        requiresMonth: false,
        allowsCustomRange: true,
        requiresYear: false,
        allowTargetTypeGrouping: false//already grouping here can't group both ways yet
    }
} as IConfiguredTrafficReport;